import type { FilePublicWithPermissions } from '@repo/types/files';

import { useRef } from 'react';

import { DraggableFile } from '@/components/features/files/dnd';
import FSEntityWrapper from '@/components/features/files/FSEntityWrapper';
import FsFileCard from '@/components/features/files/FsFileCard';
import { useFSEntityInteraction } from '@/hooks/fs';
import { useFilesStorage } from '@/store/files-store';

/**
 * Props for the File component.
 * @property {FilePublicWithPermissions} file - The file data with permissions.
 */
interface FileProps {
  file: FilePublicWithPermissions;
}

export default function File(fileProps: FileProps) {
  const { file } = fileProps;
  const fileRef = useRef<HTMLDivElement>(null);
  const { setOpenViewer, setFileToOpen } = useFilesStorage();

  const openFile = () => {
    setFileToOpen(file);
    setOpenViewer(true);
  };

  const { isEntitySelected, isFocused, size, handleClick, handleKeyDown, handleDoubleClick, longPressHandlers } = useFSEntityInteraction({
    entity: file,
    entityType: 'file',
    onOpen: openFile,
  });

  return (
    <DraggableFile file={file}>
      <FSEntityWrapper entity={file} entityType="file">
        <FsFileCard
          aria-selected={isEntitySelected}
          data-selected={isEntitySelected ? 'true' : 'false'}
          data-focused={isFocused ? 'true' : 'false'}
          tabIndex={isFocused ? 0 : -1}
          ref={fileRef}
          onClick={handleClick}
          onDoubleClick={handleDoubleClick}
          onKeyDown={handleKeyDown}
          onPointerDown={longPressHandlers.onPointerDown}
          onPointerMove={longPressHandlers.onPointerMove}
          onPointerUp={longPressHandlers.onPointerUp}
          file={file}
          disabledMenu={size > 1}
          className="aria-selected:bg-brand-accent/25 aria-selected:border-border-brand-accent aria-selected:ring-brand-accent data-[focused=true]:outline-focus-ring aria-selected:ring-3 data-[focused=true]:outline-3 data-[focused=true]:outline-offset-2"
        />
      </FSEntityWrapper>
    </DraggableFile>
  );
}
